import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Check } from "lucide-react";
import { playerService } from "../../services/api";
import { agentTenderService } from "../../services/agentTenderApi";
import CustomDropdown from "../../components/common/CustomDropdown";
import ClubVerifiedBadge from "../../components/agent-tender/ClubVerifiedBadge";
import Avatar from "../../components/common/Avatar";
import {
  CURRENCY_CHOICES,
  formatPositionValue,
  formatTypeOfTender,
  formatTransferWindow,
} from "../../constants/agentTenderChoices";

// Send a player from your portfolio in response to another agency's tender.
//
// Brief Rule 4 stays in force on this surface: the responding agent sees the
// posting AGENCY + agent and the anonymized request, never the club. The
// summary here is fed from the anonymized feed card, so there is no club field
// to leak.
const PitchToAgentTender = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [tender, setTender] = useState(location.state?.tender || null);
  const [tenderLoading, setTenderLoading] = useState(!location.state?.tender);

  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [playersError, setPlayersError] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const [message, setMessage] = useState("");
  const [isFreeAgent, setIsFreeAgent] = useState(false);
  const [currency, setCurrency] = useState("EUR");
  const [transferFee, setTransferFee] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Fill in the anonymized summary when the page is opened directly (deep link)
  // rather than from a feed card click.
  useEffect(() => {
    if (tender) return;
    let active = true;
    (async () => {
      const res = await agentTenderService.getFeedOne(id);
      if (!active) return;
      if (res.success) setTender(res.data);
      setTenderLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id, tender]);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setPlayersLoading(true);
      setPlayersError(null);
      try {
        const res = await playerService.getPlayers(new URLSearchParams(), controller.signal);
        if (res.success) setPlayers(res.data);
        else setPlayersError(res.error || "Failed to load your players.");
      } catch (err) {
        if (err?.name !== "CanceledError") setPlayersError("Failed to load your players.");
      } finally {
        setPlayersLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  // A request that asks for free players only wants players who cost nothing —
  // it is NOT a statement about whether they currently have a club (Marin,
  // 2026-08-01: "nur ablösefreie Spieler ist einfach Spieler der nichts Kostet,
  // nicht ob er keinen Verein hat, da bleibt einfach das Ablösedings weg").
  // The whole measure is this form: on such a request the amount and the
  // currency are not rendered, so nothing can be sent. Any player may be
  // pitched — "es sollen alle in der Lage sein praktisch jeden Spieler zu
  // senden" — the backend no longer gates on the club either.
  //
  // The 'Free agent' switch goes with them rather than sitting fixed on: the
  // request has already answered the question it asks, and there is no fee
  // input left for it to show or hide. Same call as the club-request form
  // (PitchPlayer.js), for the same reason.
  //
  // `only_free_players` is on the anonymized feed serializer
  // (agent_tender_serializers._SpecFieldsMixin), which is what both the card
  // hand-off and the deep-link fetch above return.
  const freePlayersOnlyRequest = Boolean(tender?.only_free_players);

  const playerName = (p) =>
    p.first_name && p.last_name
      ? `${p.first_name} ${p.last_name}`
      : p.first_name || p.last_name || "Unknown player";

  const handleSubmit = async () => {
    if (!selectedPlayer || submitting) return;
    const wyscoutId = selectedPlayer.wyscout_id || selectedPlayer.ws_player_id;
    const payload = {
      ...(wyscoutId ? { wyscout_id: wyscoutId } : { player_id: selectedPlayer.account_id }),
      content: message || `Pitching ${playerName(selectedPlayer)} for this request.`,
      // Same contract as the club-request pitch form (PitchPlayer.js): the
      // switch is TRANSMITTED, never re-derived downstream from a missing fee.
      // This surface writes the same Pitch rows and feeds the same
      // `free_agent` flag on the response row, so leaving it out would keep
      // exactly the same false "Free Agent" claim alive here.
      // On a free-players-only request the switch is not on screen; the answer
      // then comes from the request, which is fee-free by definition.
      is_free_agent: freePlayersOnlyRequest ? true : isFreeAgent,
    };
    if (!freePlayersOnlyRequest && !isFreeAgent && transferFee) {
      payload.transfer_fee = transferFee;
      payload.transfer_fee_currency = currency;
    }
    setSubmitting(true);
    const res = await agentTenderService.pitch(id, payload);
    setSubmitting(false);
    if (res.success) {
      setDone(true);
    } else {
      toast.error(res.error || "We couldn't send this player. Please try again.");
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Player pitched</h2>
          <p className="text-gray-600 mb-6">
            Your player has been pitched to the posting agency. We'll let you know
            if they accept and open a chat.
          </p>
          <button
            onClick={() => navigate("/agent-tenders")}
            className="w-full py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
          >
            Back to agent requests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-2xl font-semibold mb-1">Pitch Player</h1>
        <p className="text-sm text-gray-500 mb-6">
          Choose a player from your portfolio to pitch for this request.
        </p>

        {/* Anonymized request summary (never a club) */}
        <div className="bg-white rounded-lg border border-[#D2D4D880] p-5 mb-6">
          {tenderLoading ? (
            <p className="text-gray-500 text-sm">Loading request…</p>
          ) : tender ? (
            <>
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="text-lg font-semibold capitalize">
                  {formatPositionValue(tender.position, tender.other_staff_position)}
                </h3>
                <ClubVerifiedBadge status={tender.verification_status} />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Avatar
                  src={tender.responsible_agent?.avatar}
                  fallbackText={tender.responsible_agent?.display_name}
                  size={20}
                />
                <span className="text-xs text-[#535370]">
                  {tender.posted_agency?.name}
                  {tender.responsible_agent?.display_name
                    ? ` · ${tender.responsible_agent.display_name}`
                    : ""}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-gray-500">Request type</span>
                <span className="text-right font-medium capitalize">
                  {formatTypeOfTender(tender.type_of_tender)}
                </span>
                {(() => {
                  const w = formatTransferWindow(tender.transfer_window);
                  if (!w) return null;
                  return (
                    <>
                      <span className="text-gray-500">Transfer window</span>
                      <span className="text-right font-medium">
                        {[w.label, w.dates].filter(Boolean).join(" · ")}
                      </span>
                    </>
                  );
                })()}
                {(tender.commission_split || tender.commission_split_note) && (
                  <>
                    <span className="text-gray-500">Agent commission split</span>
                    <span className="text-right font-medium">
                      {tender.commission_split || tender.commission_split_note}
                    </span>
                  </>
                )}
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-sm">This request is no longer available.</p>
          )}
        </div>

        {/* Player picker */}
        <div className="bg-white rounded-lg border border-[#D2D4D880] p-5 mb-6">
          <h3 className="font-semibold mb-3">Select a player</h3>
          {playersLoading ? (
            <p className="text-gray-500 text-sm">Loading your players…</p>
          ) : playersError ? (
            <p className="text-red-500 text-sm">{playersError}</p>
          ) : players.length === 0 ? (
            <p className="text-gray-500 text-sm">
              You don't have any players in your portfolio yet.
            </p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {players.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPlayer(p)}
                  className={`w-full flex items-center gap-3 p-3 border rounded-lg text-left ${
                    selectedPlayer?.id === p.id ? "border-[#2283FF] bg-blue-50" : "border-gray-200"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                      selectedPlayer?.id === p.id ? "border-[#2283FF] border" : "border-2 border-gray-300"
                    }`}
                  >
                    {selectedPlayer?.id === p.id && (
                      <span className="w-2 h-2 bg-[#2283FF] rounded-full" />
                    )}
                  </span>
                  <img
                    src={p.player_image || "/placeholder-player.png"}
                    alt={playerName(p)}
                    className="w-9 h-9 rounded-full object-cover bg-gray-100"
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{playerName(p)}</p>
                    <p className="text-xs text-gray-500">{p.position || ""}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Optional message + fee */}
        <div className="bg-white rounded-lg border border-[#D2D4D880] p-5 mb-6 space-y-4">
          {freePlayersOnlyRequest ? (
            <p className="text-sm text-gray-500">
              This request is for free players only, so no transfer fee applies.
            </p>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium">Free agent</span>
                <p className="text-xs text-gray-500">Turn on if no transfer fee applies.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isFreeAgent}
                onClick={() => setIsFreeAgent((v) => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isFreeAgent ? "bg-[#2283FF]" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    isFreeAgent ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          )}

          {!freePlayersOnlyRequest && !isFreeAgent && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Currency</label>
                <CustomDropdown
                  options={CURRENCY_CHOICES}
                  value={currency}
                  onChange={setCurrency}
                  displayProperty="label"
                  valueProperty="value"
                  className="w-full px-3 py-2 border rounded-lg"
                  dropdownClassName="rounded-[9px] border-[#E2E8F0]"
                  optionClassName="!px-3 !py-2 !text-[13.5px] !font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Transfer fee</label>
                <input
                  type="text"
                  value={transferFee}
                  onChange={(e) => setTransferFee(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="Amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a short note about this player"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[90px]"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedPlayer || submitting}
            className="px-5 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {submitting ? "Pitching…" : "Pitch player"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PitchToAgentTender;
